import { useEffect, useState } from "react";
import { Loader2, CalendarX } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatTime } from "@/lib/format";

export default function SlotPicker({ providerId, serviceId, date, time, onDate, onTime }) {
  const [dates, setDates] = useState(null);
  const [slots, setSlots] = useState(null);

  useEffect(() => {
    if (!serviceId) return;
    setDates(null);
    api.get(`/providers/${providerId}/available-dates`, { params: { service_id: serviceId, days: 28 } })
      .then(({ data }) => setDates(data)).catch(() => setDates([]));
  }, [providerId, serviceId]);

  useEffect(() => {
    if (!date || !serviceId) return setSlots(null);
    setSlots(null);
    api.get(`/providers/${providerId}/slots`, { params: { service_id: serviceId, date } })
      .then(({ data }) => setSlots(data.slots)).catch(() => setSlots([]));
  }, [providerId, serviceId, date]);

  if (!dates) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  if (!dates.length)
    return (
      <div className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500" data-testid="no-dates">
        <CalendarX className="mx-auto mb-2 h-8 w-8 text-slate-300" /> No availability in the next 4 weeks.
      </div>
    );

  return (
    <div className="space-y-6">
      <DateStrip dates={dates} selected={date} onSelect={(d) => { onDate(d); onTime(null); }} />
      {date && (
        <div>
          <p className="mb-3 text-sm font-semibold text-slate-900">Available times on {formatDate(date)}</p>
          <SlotGrid slots={slots} selected={time} onSelect={onTime} />
        </div>
      )}
    </div>
  );
}

function DateStrip({ dates, selected, onSelect }) {
  return (
    <div>
      <p className="mb-3 text-sm font-semibold text-slate-900">Select a date</p>
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2" data-testid="date-list">
        {dates.map((d) => (
          <button
            key={d.date}
            type="button"
            data-testid={`date-option-${d.date}`}
            onClick={() => onSelect(d.date)}
            className={`flex min-w-[76px] flex-col items-center rounded-xl border px-3 py-3 transition-all ${d.date === selected ? "border-sky-600 bg-sky-600 text-white shadow-md" : "border-slate-200 bg-white text-slate-700 hover:border-sky-300"}`}
          >
            <span className="text-xs font-medium uppercase">{formatDate(d.date, "EEE")}</span>
            <span className="font-heading text-xl font-bold">{formatDate(d.date, "d")}</span>
            <span className="text-xs">{formatDate(d.date, "MMM")}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SlotGrid({ slots, selected, onSelect }) {
  if (!slots) return <Loader2 className="h-5 w-5 animate-spin text-sky-600" />;
  if (slots.length === 0) return <p className="text-sm text-slate-500" data-testid="no-slots">No times left on this date.</p>;
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5" data-testid="slot-list">
      {slots.map((s) => (
        <button
          key={s}
          type="button"
          data-testid={`slot-option-${s}`}
          onClick={() => onSelect(s)}
          className={`h-12 rounded-xl border text-sm font-semibold transition-all ${selected === s ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-teal-400"}`}
        >
          {formatTime(s)}
        </button>
      ))}
    </div>
  );
}
